import { useMemo } from "react";

import { useKYCResults } from "../../../api/hooks/kyc/useKYCResults";
import {
  DateRangeFilterInput,
  KycResultFilter,
  KycResultOrderBy,
  OrderBy,
} from "../../../graphql/generated";
import { EnquiryType, KYCStatus } from "../types";

interface UseKYCDataProps {
  page?: number;
  limit?: number;
  sortField?: string;
  sortDirection?: "asc" | "desc";
  filters?: {
    dateRange?: DateRangeFilterInput;
    customerId?: string | null;
  };
}

export const useKYCData = ({
  page = 1,
  limit = 10,
  sortField = "createdAt",
  sortDirection = "desc",
  filters,
}: UseKYCDataProps = {}) => {
  const filter: KycResultFilter = useMemo(() => {
    const filterObj: KycResultFilter = {};

    if (filters?.dateRange) {
      filterObj.createdAt = filters.dateRange;
    }

    if (filters?.customerId) {
      filterObj.customerId = {
        equals: Number(filters.customerId),
      };
    }

    return filterObj;
  }, [filters]);

  const orderBy: KycResultOrderBy = useMemo(() => {
    const direction = sortDirection === "asc" ? OrderBy.Asc : OrderBy.Desc;

    switch (sortField) {
      case "requestDate":
      case "createdAt":
        return { createdAt: direction };
      case "companyName":
        return { customer: { name: direction } };
      default:
        return { createdAt: direction };
    }
  }, [sortField, sortDirection]);

  const [{ data: kycResponse, fetching: loading, error }, refetch] =
    useKYCResults({
      page,
      limit,
      filter,
      orderBy,
    });

  const data = useMemo(() => {
    if (!kycResponse?.kycResults) return null;

    const apiData = kycResponse.kycResults;

    const transformedData = apiData.data.map((item) => ({
      id: item.id,
      requestDate: item.createdAt || new Date().toISOString(),
      companyName: item.customer?.name || "Unknown Company",
      customerName: item.customer?.name || "Unknown Company",
      customerId: item.customerId,
      status: item.status || KYCStatus.ONGOING,
      enquiryType: EnquiryType.COMPANY,
      error: item.error,
      comments: (item.comment as Record<string, unknown>[]) || [],
      createdAt: item.createdAt || new Date().toISOString(),
      updatedAt: item.createdAt || new Date().toISOString(),
    }));

    return {
      data: transformedData,
      total: apiData.total,
      page,
      limit,
    };
  }, [kycResponse, page, limit]);

  return {
    data,
    loading,
    error,
    refetch,
  };
};
